import { lazy, Suspense, useEffect, useState } from "react";
import { Button, Flex, Grid, Modal, Spin, Typography } from "antd";
import {
  CloseOutlined,
  QrcodeOutlined,
  ScanOutlined,
} from "@ant-design/icons";

import QRcodeGereateSocketOuth from "../qrCodeGereateSocketOuth/qrCodeGereateSocketOuth";

// Сканер (react-qr-scanner, около 0,5 МБ) нужен, только когда человек нажал «Сканировать»:
// отдельный чанк, чтобы не грузить его вместе с лендингом.
const QRScannerOuth = lazy(() => import("../qrScannerOuth/qrsCannerOuth"));

const { Text, Title } = Typography;

function getSystemDarkMode() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export default function LendingQrScannModalBTN({
  variant = "icon",
  isDarkMode,
} = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [systemDark, setSystemDark] = useState(getSystemDarkMode);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => setSystemDark(event.matches);

    setSystemDark(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const isDark = typeof isDarkMode === "boolean" ? isDarkMode : systemDark;

  const modalBg = isDark
    ? `
      radial-gradient(circle at top left, rgba(59, 130, 246, 0.22), transparent 34%),
      radial-gradient(circle at bottom right, rgba(124, 58, 237, 0.18), transparent 32%),
      linear-gradient(135deg, #111827 0%, #181B22 52%, #0F172A 100%)
    `
    : `
      radial-gradient(circle at top left, rgba(59, 121, 246, 0.12), transparent 34%),
      radial-gradient(circle at bottom right, rgba(124, 58, 237, 0.08), transparent 32%),
      linear-gradient(135deg, #FFFFFF 0%, #F7FAFF 52%, #EEF4FF 100%)
    `;

  const overlayBg = isDark
    ? "rgba(2, 6, 23, 0.72)"
    : "rgba(15, 23, 42, 0.34)";

  const modalBorder = isDark
    ? "rgba(148, 163, 184, 0.32)"
    : "rgba(25, 73, 160, 0.14)";

  const modalShadow = isDark
    ? "0 24px 90px rgba(0, 0, 0, 0.52)"
    : "0 24px 80px rgba(18, 62, 140, 0.18)";

  const iconBg = isDark
    ? "rgba(59, 130, 246, 0.16)"
    : "rgba(59, 121, 246, 0.10)";

  const iconBorder = isDark
    ? "rgba(96, 165, 250, 0.28)"
    : "rgba(59, 121, 246, 0.18)";

  const textColor = isDark ? "#F9FAFB" : "#172033";
  const mutedColor = isDark ? "#CBD5E1" : "rgba(27, 43, 65, 0.68)";

  const segmentBg = isDark
    ? "rgba(15, 23, 42, 0.72)"
    : "rgba(255, 255, 255, 0.78)";

  const segmentBorder = isDark
    ? "rgba(148, 163, 184, 0.18)"
    : "rgba(25, 73, 160, 0.12)";

  const inactiveButtonColor = isDark ? "#CBD5E1" : "#475569";

  const inactiveHoverBg = isDark
    ? "rgba(59, 130, 246, 0.14)"
    : "rgba(59, 121, 246, 0.08)";

  const closeColor = isDark ? "#E5E7EB" : "rgba(23, 32, 51, 0.62)";

  const closeModal = () => {
    setIsOpen(false);
    setIsScannerActive(false);
  };

  return (
    <>
      {variant === "full" ? (
        <Button
          type="default"
          onClick={() => setIsOpen(true)}
          icon={<QrcodeOutlined />}
          className={`qrLoginButton ${isDark ? "isDark" : ""}`}
        >
          Войти по QR-коду
        </Button>
      ) : variant === "compact" ? (
        <Button
          type="text"
          onClick={() => setIsOpen(true)}
          icon={<QrcodeOutlined />}
          aria-label="Войти по QR-коду"
          className={`qrLoginIconButton ${isDark ? "isDark" : ""}`}
        />
      ) : (
        <Button
          type="text"
          onClick={() => setIsOpen(true)}
          icon={<QrcodeOutlined />}
          aria-label="Войти по QR-коду"
          style={{
            width: 50,
            height: 50,
            minWidth: 56,
            flex: "0 0 56px",
            padding: 0,
            borderRadius: 18,
            background: isDark
              ? "rgba(59, 130, 246, 0.16)"
              : "rgba(59, 121, 246, 0.10)",
            color: "#3B79F6",
            border: `1px solid ${iconBorder}`,
            boxShadow: isDark
              ? "0 8px 24px rgba(0, 0, 0, 0.22)"
              : "0 10px 24px rgba(59, 121, 246, 0.12)",
            fontSize: 22,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        />
      )}

      <Modal
        open={isOpen}
        onCancel={closeModal}
        centered
        footer={null}
        width={560}
        destroyOnClose
        closeIcon={
          <CloseOutlined
            style={{
              color: closeColor,
              fontSize: 16,
              fontWeight: 900,
            }}
          />
        }
        styles={{
          mask: {
            background: overlayBg,
            backdropFilter: "blur(10px)",
          },
          content: {
            padding: 0,
            borderRadius: 28,
            background: modalBg,
            border: `1px solid ${modalBorder}`,
            overflow: "hidden",
            boxShadow: modalShadow,
          },
          body: {
            padding: isMobile ? 16 : 22,
          },
        }}
      >
        <Flex vertical gap={18}>
          <Flex gap={14} align="flex-start" style={{ paddingRight: 34 }}>
            <Flex
              align="center"
              justify="center"
              style={{
                width: 48,
                minWidth: 48,
                height: 48,
                borderRadius: 16,
                background: iconBg,
                color: "#3B79F6",
                border: `1px solid ${iconBorder}`,
                fontSize: 23,
              }}
            >
              {isScannerActive ? <ScanOutlined /> : <QrcodeOutlined />}
            </Flex>

            <Flex vertical gap={6} style={{ minWidth: 0 }}>
              <Title
                level={4}
                style={{
                  margin: 0,
                  fontSize: isMobile ? 20 : 22,
                  lineHeight: "28px",
                  fontWeight: 900,
                  color: textColor,
                }}
              >
                {isScannerActive
                  ? "Сканируйте QR-код для входа"
                  : "Ваш QR-код для авторизации"}
              </Title>

              <Text
                style={{
                  fontSize: 15,
                  lineHeight: "22px",
                  fontWeight: 700,
                  color: mutedColor,
                }}
              >
                {isScannerActive
                  ? 'Откройте раздел "Приложение" на другом устройстве и наведите камеру на QR-код.'
                  : "Покажите этот QR-код устройству, с которого нужно выполнить авторизацию."}
              </Text>
            </Flex>
          </Flex>

          <Flex
            gap={8}
            style={{
              width: "100%",
              padding: 5,
              borderRadius: 18,
              background: segmentBg,
              border: `1px solid ${segmentBorder}`,
            }}
          >
            <Button
              block
              icon={<QrcodeOutlined />}
              onClick={() => setIsScannerActive(false)}
              style={{
                height: 46,
                borderRadius: 14,
                border: "none",
                fontSize: 15,
                fontWeight: 900,
                background: !isScannerActive ? "#3B79F6" : "transparent",
                color: !isScannerActive ? "#FFFFFF" : inactiveButtonColor,
                boxShadow: !isScannerActive
                  ? "0 10px 22px rgba(59, 121, 246, 0.28)"
                  : "none",
              }}
              onMouseEnter={(event) => {
                if (isScannerActive) {
                  event.currentTarget.style.background = inactiveHoverBg;
                }
              }}
              onMouseLeave={(event) => {
                if (isScannerActive) {
                  event.currentTarget.style.background = "transparent";
                }
              }}
            >
              QR-код
            </Button>

            <Button
              block
              icon={<ScanOutlined />}
              onClick={() => setIsScannerActive(true)}
              style={{
                height: 46,
                borderRadius: 14,
                border: "none",
                fontSize: 15,
                fontWeight: 900,
                background: isScannerActive ? "#3B79F6" : "transparent",
                color: isScannerActive ? "#FFFFFF" : inactiveButtonColor,
                boxShadow: isScannerActive
                  ? "0 10px 22px rgba(59, 121, 246, 0.28)"
                  : "none",
              }}
              onMouseEnter={(event) => {
                if (!isScannerActive) {
                  event.currentTarget.style.background = inactiveHoverBg;
                }
              }}
              onMouseLeave={(event) => {
                if (!isScannerActive) {
                  event.currentTarget.style.background = "transparent";
                }
              }}
            >
              Сканировать
            </Button>
          </Flex>

          {isScannerActive ? (
            <Suspense fallback={<Spin />}>
              <QRScannerOuth isDarkMode={isDark} />
            </Suspense>
          ) : (
            <QRcodeGereateSocketOuth
              isScannerActive={isScannerActive}
              isDarkMode={isDark}
            />
          )}
        </Flex>
      </Modal>
    </>
  );
}
